import {
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  ReactElement,
  isValidElement,
  useEffect,
  useState,
} from 'react';
import { useFormContext, RegisterOptions } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useIntl } from 'react-intl';
import { InputWrapper } from './styles';
import parse from 'html-react-parser';

export interface IProps
  extends Pick<
    InputHTMLAttributes<HTMLInputElement>,
    'maxLength' | 'disabled' | 'min' | 'max'
  > {
  name: string;
  registerOptions?: RegisterOptions;
  type?: Exclude<
    HTMLInputTypeAttribute,
    'range' | 'reset' | 'submit' | 'search'
  >;
  placeholder?: string;
  iconPlaceholder?: string | ReactElement;
  label?: string;
  readonly?: boolean;
  labelVisibility?: boolean;
  className?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onlyNumber?: boolean;
  inputClassname?: string;
  inputStyle?: React.CSSProperties;
  englishOnly?: boolean;
  englishLettersAndNumOnly?: boolean;
  englishLettersAndNumAndDashAndUnderScoreOnly?: boolean;
  showRequiredSign?: boolean;
  hideInputErrorMsg?: boolean;
  questionForm?: boolean;
  maxLength?: number;
  logInput?: boolean;
}

export function Input({
  name,
  registerOptions,
  type = 'text',
  placeholder,
  iconPlaceholder,
  label,
  readonly,
  labelVisibility = true,
  className,
  disabled,
  min = 0,
  maxLength,
  max,
  value,
  defaultValue,
  onChange,
  onlyNumber = false,
  inputClassname = '',
  englishOnly = false,
  englishLettersAndNumOnly = false,
  englishLettersAndNumAndDashAndUnderScoreOnly = false,
  inputStyle = {},
  showRequiredSign = true,
  hideInputErrorMsg = false,
  questionForm = false,
  logInput = false,
}: IProps) {
  const { setValue, register, formState, watch } = useFormContext();
  const { errors } = formState;
  const { formatMessage } = useIntl();
  const [matches, setMatches] = useState(
    window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    window
      .matchMedia('(min-width: 768px)')
      .addEventListener('change', (e) => setMatches(e.matches));
  }, []);

  // onChange Handler
  const onChangeHandler = (Event: React.ChangeEvent<HTMLInputElement>) => {
    const element = {
      ...(register && { ...register(`${name}`, registerOptions) }),
    };
    if(englishLettersAndNumAndDashAndUnderScoreOnly){
      setValue(name, Event.target.value.replace(/[^A-Za-z0-9\-_]/gi, ''));
    }
    if (englishLettersAndNumOnly) {
      setValue(name, Event.target.value.replace(/[^A-Za-z0-9]/gi, ''));
    }
    if (englishOnly) {
      setValue(name, Event.target.value.replace(/[^A-Za-z]/gi, ''));
    }
    if (onlyNumber) {
      if (Event.target.value !== '') {
        setValue(name, +Event.target.value.replace(/\D/g, '') || '');
      }
    } else {
      if (Object.keys(element).length !== 0) element.onChange(Event);
      if (onChange) onChange(Event);
    }
  };
  return (
    <InputWrapper className={className}>
      <div className={questionForm ? 'label questionForm' : 'label'}>
        {label && <p>{label}</p>}
        <div
          style={{
            position: 'relative',
          }}
        >
          <input
            type={type}
            placeholder={placeholder}
            {...register(name, registerOptions)}
            readOnly={readonly}
            disabled={disabled}
            min={min}
            max={max}
            maxLength={maxLength}
            value={value}
            defaultValue={defaultValue}
            onChange={onChangeHandler}
            className={`${inputClassname}${errors[name] ? 'hasError' : ''}`}
          />
          {iconPlaceholder &&
            (isValidElement(iconPlaceholder) ? (
              iconPlaceholder
            ) : (
              <div className={`icon ${label ? 'label' : 'no_label'}`}>
                <span>{formatMessage({ id: iconPlaceholder as string })}</span>
              </div>
            ))}
        </div>
      </div>

      {type === 'hidden' &&
        !watch(name)?.length &&
        !!errors &&
        !!errors[name] && (
          <div className="ErrorMessageStyle">
            <ErrorMessage
              errors={errors}
              name={name}
              render={({ message }) => {
                const newlineText = message.replace(/\n/g, '<br />');
                return <p>{parse(newlineText)}</p>;
              }}
            />
          </div>
        )}
      {type !== 'hidden' &&
        !!errors &&
        !!errors[name] &&
        !hideInputErrorMsg && (
          <div className="ErrorMessageStyle">
            <ErrorMessage
              errors={errors}
              name={name}
              render={({ message }) => {
                const newlineText = message.replace(/\n/g, '<br />');
                return <p>{parse(newlineText)}</p>;
              }}
            />
          </div>
        )}
    </InputWrapper>
  );
}
